import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { ComponentReplacementCost } from '../../models/ReplacementCostSummary';
import { useComponentReplacementCostById, useUpdateComponentReplacementCost } from '../../hooks/UseReplacementCost';
import { Component } from '../../models/Component';
import { useAssetByComponentId } from '../../hooks/UseAsset';
import { Asset } from '../../models/Asset';
import { ErrorMessage } from '../../components/ErrorMessage';

export const ComponentReplacementCostEdit: React.FC = () => {
  const { id = '', componentId = '' } = useParams<{ id: string; componentId: string }>();
  const history = useNavigate();
  const [component, setComponent] = useState<Component>({} as Component);
  const [adjustmentPct, setAdjustmentPct] = useState<number>(0);
  const [area, setArea] = useState<number>(0);
  const { data: componentReplacementCost = {} as ComponentReplacementCost } = useComponentReplacementCostById(parseInt(id));
  const { data: asset = {} as Asset } = useAssetByComponentId(parseInt(componentId));
  const updateComponentReplacementCost = useUpdateComponentReplacementCost();
  const areaOptions: string[] = ['sqm', 'lm', 'cum', 'km', 'no.'];

  const ComponentReplacementCostSchema = yup.object().shape({
    name: yup.string().max(100, 'Maximum length is 100.').required('Item Type is required'),
    length: yup.number().positive().label('Length').min(0).required('Please enter a valid length'),
    width: yup.number().positive().label('Width').min(0).required('Please enter a valid width'),
    quantity: yup.number().positive().label('Quantity').min(1).required('Please enter a valid quantity'),
    localityFactorPct: yup.number().positive().label('Locality Factor').min(0),
    specifiedRate: yup
      .number()
      .positive()
      .label('Specified Rate')
      .nullable()
      .transform((_, val) => (val === Number(val) ? val : null)),
  });

  const {
    register,
    setValue,
    handleSubmit,
    control,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm<ComponentReplacementCost>({
    defaultValues: { id: parseInt(id), area: 0 },
    reValidateMode: 'onBlur',
    resolver: yupResolver(ComponentReplacementCostSchema),
  });

  const adjustedRateWatch: number = watch('adjustedRate');

  const widthWatch: number = watch('width');
  const lengthWatch: number = watch('length');

  useEffect(() => {
    if (Number.isNaN(adjustedRateWatch) || adjustedRateWatch === 0) {
      setAdjustmentPct(0);
    } else if (adjustedRateWatch != undefined && Number(id) !== 0) {
      setAdjustmentPct((adjustedRateWatch / componentReplacementCost.unitRate - 1) * 100);
    } else if (component.componentAssumptions != undefined) {
      setAdjustmentPct((adjustedRateWatch / component.componentAssumptions.unitRate - 1) * 100);
    }
  }, [adjustedRateWatch]);

  useEffect(() => {
    if (Object.keys(componentReplacementCost).length > 0 && Number(id) !== 0) {
      reset({
        ...componentReplacementCost,
        localityFactorPct: componentReplacementCost.localityFactorPct * 100,
        adjustmentPct: componentReplacementCost.adjustmentPct * 100,
        adjustedRate: componentReplacementCost.adjustedRate,
      });
      setAdjustmentPct(componentReplacementCost.adjustmentPct * 100);
    }
  }, [componentReplacementCost]);

  useEffect(() => {
    setArea(widthWatch * lengthWatch);
  }, [widthWatch, lengthWatch]);

  useEffect(() => {
    if (Object.keys(asset).length > 0 && Number(id) === 0) {
      const componentFind = asset.components.find((component) => component.id === parseInt(componentId));
      setComponent(componentFind || ({} as Component));
    }
  }, [asset]);

  useEffect(() => {
    if (Object.keys(component).length > 0 && Number(id) === 0) {
      reset({
        unitRate: component.componentAssumptions?.unitRate,
        localityFactorPct:
          component.componentAssumptions?.localityFactorPct != undefined
            ? component.componentAssumptions.localityFactorPct * 100
            : 0,
        adjustedRate: component.componentAssumptions?.unitRate,
      });
    }
  }, [component]);

  const onSubmit = handleSubmit((formComponentReplacementCost) => {
    updateComponentReplacementCost.mutate(
      {
        ...formComponentReplacementCost,
        unitRate:
          formComponentReplacementCost.id === 0 && component.componentAssumptions !== undefined
            ? component.componentAssumptions?.unitRate
            : componentReplacementCost.unitRate,
        componentId: formComponentReplacementCost.id === 0 ? Number(componentId) : formComponentReplacementCost.componentId,
        localityFactorPct: formComponentReplacementCost.localityFactorPct / 100,
        adjustmentPct: adjustmentPct / 100,
      },
      {
        onSuccess: (data, variables, context) => {
          history(-1);
        },
      }
    );
  });

  return (
    <form onSubmit={onSubmit} className="mx-4 space-y-6">
      <div className="bg-white shadow mt-4 px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">General</h3>
            {/* <p className="mt-1 text-sm text-gray-500">
              Use a permanent address where you can receive mail.
            </p> */}
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <input type="hidden" {...register('id', { valueAsNumber: true })} />
            <input type="hidden" {...register('componentId', { valueAsNumber: true })} />

            <div className="bg-white space-y-6">
              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="areaType" className="block text-sm font-medium text-gray-700">
                  Area Type
                </label>
                <div className="flex space-x-2 items-baseline">
                  <div className="lg:w-1/3">
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <select
                        {...register('areaType')}
                        className="block w-full w-full rounded-md border-gray-300 bg-none text-left shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:px-1 sm:text-sm lg:px-2"
                      >
                        {areaOptions.map((areaType) => (
                          <option key={areaType} value={areaType}>
                            {areaType}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="class" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <div className="flex space-x-2 items-baseline">
                  <div className="lg:w-1/3">
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="text"
                        {...register('name')}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
                {errors?.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
              </div>
            </div>

            {/* <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="submit"
                  className="bg-indigo-600 border border-transparent shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Save
                </button>
              </div> */}
          </div>
        </div>
      </div>
      <div className="bg-white shadow mt-4 px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Dimensions</h3>
            {/* <p className="mt-1 text-sm text-gray-500">
              Use a permanent address where you can receive mail.
            </p> */}
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <div className="bg-white space-y-6">
              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="class" className="block text-sm font-medium text-gray-700">
                  Length
                </label>
                <div className="flex space-x-2 items-baseline">
                  <div>
                    <div className="mt-1 relative rounded-md shadow-sm lg:w-24">
                      <input
                        type="number"
                        step="any"
                        {...register('length', { valueAsNumber: true })}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
                {errors?.length && <ErrorMessage>{errors.length.message}</ErrorMessage>}
              </div>

              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="class" className="block text-sm font-medium text-gray-700">
                  Width
                </label>
                <div className="flex space-x-2 items-baseline">
                  <div>
                    <div className="mt-1 relative rounded-md shadow-sm lg:w-24">
                      <input
                        type="number"
                        step="any"
                        {...register('width', { valueAsNumber: true })}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
                {errors?.width && <ErrorMessage>{errors.width.message}</ErrorMessage>}
              </div>

              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="class" className="block text-sm font-medium text-gray-700">
                  Area
                </label>
                <div className="flex space-x-2 items-baseline">
                  <div>
                    <div className="mt-1 relative rounded-md shadow-sm lg:w-24">
                      <input
                        type="number"
                        disabled
                        {...register('area', { valueAsNumber: true })}
                        value={area}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block bg-gray-100 w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
                {errors?.area && <ErrorMessage>{errors.area.message}</ErrorMessage>}
              </div>

              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="class" className="block text-sm font-medium text-gray-700">
                  Quantity
                </label>
                <div className="flex space-x-2 items-baseline">
                  <div>
                    <div className="mt-1 relative rounded-md shadow-sm lg:w-40">
                      <input
                        type="number"
                        step="any"
                        {...register('quantity', { valueAsNumber: true })}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
                {errors?.quantity && <ErrorMessage>{errors.quantity.message}</ErrorMessage>}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white shadow mt-4 px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Values</h3>
            {/* <p className="mt-1 text-sm text-gray-500">Use a permanent address where you can receive mail.</p> */}
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <div className="bg-white space-y-6">
              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="class" className="block text-sm font-medium text-gray-700">
                  Unit Rate
                </label>
                <div className="flex space-x-2 items-baseline lg:w-1/4">
                  <div className="flex-grow">
                    <div className="mt-1 relative rounded-md shadow-sm ">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        disabled
                        {...register('unitRate', { valueAsNumber: true })}
                        value={getValues('unitRate')}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full bg-gray-100 pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                        aria-describedby="price-currency"
                      />
                    </div>
                  </div>
                  {/* <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-light bg-indigo-100 text-indigo-800">
                    $120
                  </span> */}
                </div>
              </div>

              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="class" className="block text-sm font-medium text-gray-700">
                  Adjusted Rate
                </label>
                <div className="flex space-x-2 items-baseline lg:w-1/4">
                  <div className="flex-grow">
                    <div className="mt-1 relative rounded-md shadow-sm ">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        step="any"
                        {...register('adjustedRate', { valueAsNumber: true })}
                        defaultValue={
                          Object.keys(componentReplacementCost).length > 0
                            ? ((componentReplacementCost.adjustmentPct + 1) * componentReplacementCost.unitRate).toFixed(2)
                            : 0
                        }
                        id="price"
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                        aria-describedby="price-currency"
                      />
                    </div>
                    {adjustedRateWatch < 0 && <ErrorMessage>Adjusted Rate cannot be less than 0</ErrorMessage>}
                  </div>
                  {/* <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-light bg-indigo-100 text-indigo-800">
                    $120
                  </span> */}
                </div>
              </div>

              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="class" className="block text-sm font-medium text-gray-700">
                  Locality Factor
                </label>
                <div className="flex space-x-2 items-baseline">
                  <div>
                    <div className="mt-1 relative rounded-md shadow-sm lg:w-24">
                      <input
                        type="number"
                        step="any"
                        {...register('localityFactorPct', { valueAsNumber: true })}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {/* Heroicon name: solid/question-mark-circle */}
                        <span className="text-gray-500 sm:text-sm">%</span>
                      </div>
                    </div>
                    {errors?.localityFactorPct && <ErrorMessage>{errors.localityFactorPct.message}</ErrorMessage>}
                  </div>
                  {/* <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-light bg-indigo-100 text-indigo-800">
                    5%
                  </span> */}
                </div>
              </div>

              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="class" className="block text-sm font-medium text-gray-700">
                  Adjustment Pct
                </label>
                <div className="flex space-x-3 items-baseline">
                  <div>
                    <div className="mt-1 relative rounded-md shadow-sm lg:w-30">
                      <input
                        disabled
                        type="number"
                        {...register('adjustmentPct', { valueAsNumber: true })}
                        value={adjustmentPct.toFixed(6)}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block bg-gray-100 w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {/* Heroicon name: solid/question-mark-circle */}
                        <span className="text-gray-500 sm:text-sm">%</span>
                      </div>
                    </div>
                  </div>
                  {/* <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-light bg-indigo-100 text-indigo-800">
                    2%
                  </span> */}
                </div>
              </div>

              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="class" className="block text-sm font-medium text-gray-700">
                  Specified Rate
                </label>
                <div className="flex space-x-2 items-baseline lg:w-1/4">
                  <div className="flex-grow">
                    <div className="mt-1 relative rounded-md shadow-sm ">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        step="any"
                        {...register('specifiedRate', { valueAsNumber: true })}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                        aria-describedby="price-currency"
                      />
                    </div>
                    {errors?.specifiedRate && <ErrorMessage>{errors.specifiedRate.message}</ErrorMessage>}
                  </div>
                  {/* <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-light bg-indigo-100 text-indigo-800">
                    $120
                  </span> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="pb-2">
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={(): void => {
              history(-1);
            }}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Save
          </button>
        </div>
      </div>
    </form>
  );
};
